import { useContext, useEffect, useRef, useState, type FunctionComponent } from "react";
import { Button, Caption1, Link, Subtitle1, Title3, Toaster, makeStyles, mergeClasses, shorthands, tokens } from "@fluentui/react-components";
import walterLogo from "@src/assets/images/logos/production/logo-64.png";
import { createAuthMessageHandler, requestDeviceCodeUrl, getParentDomain } from "@shared/lib/auth/xframe-communication";
import { tokenStorage } from "../../lib/settings/tokenStorage";
import Progress from "./Progress";
import { captureErrorInfo } from "@src/taskpane/helpers/errorHandler";
import useToasts from "@src/taskpane/hooks/useToasts";
import { WalterContext } from "../providers/walter";
import { WALTER_HOST, WORD_OAUTH_CLIENT_ID } from "@src/lib/env-vars";

export const deviceAuthorizationUrl = `${WALTER_HOST}/device_authorizations/${WORD_OAUTH_CLIENT_ID}`;
export const deviceAutoSignInUrl = `${WALTER_HOST}/device_authorizations/${WORD_OAUTH_CLIENT_ID}/auto`;

export const useStyles = makeStyles({
  inner: {
    display: "flex",
    minHeight: "100vh",
    flexDirection: "column",
    alignItems: "center",
  },
  hero: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    width: "100%",
    ...shorthands.padding("48px", "24px", "40px", "24px"),
    backgroundColor: tokens.colorNeutralBackground3,
  },
  logo: {
    width: "64px",
    height: "64px",
    ...shorthands.margin("0", "0", "12px", "0"),
  },
  tagline: {
    color: tokens.colorNeutralForeground3,
    ...shorthands.margin("4px", "0", "0", "0"),
  },
  actions: {
    display: "flex",
    flexDirection: "column",
    alignItems: "stretch",
    alignSelf: "stretch",
    ...shorthands.padding("24px", "24px", "0", "24px"),
  },
  footer: {
    marginTop: "auto",
    ...shorthands.padding("0", "24px", "20px", "24px"),
  },
  footerText: {
    color: tokens.colorNeutralForeground4,
    textAlign: "center",
  },
  textCenter: {
    textAlign: "center",
    ...shorthands.padding("12px"),
  },
});

export const LoginPage: FunctionComponent = ({ children }) => {
  const styles = useStyles();
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [deviceAuthSignInUrl, setDeviceAuthSignInUrl] = useState("");
  const [deviceCodeExpiryDurationMS, setDeviceCodeExpiryDurationMS] = useState<number>();
  const [error, setError] = useState<string>("");
  const { toasterId, setIsLoggedIn } = useContext(WalterContext);
  const { showErrors } = useToasts();
  const [signInLoaded, setSignInLoaded] = useState(false);

  // Device codes expire, so request a new one when the previous one expires
  useEffect(() => {
    if (!deviceCodeExpiryDurationMS || !iframeRef.current) {
      return;
    }
    const intervalId = window.setInterval(() => {
      if (iframeRef.current) {
        requestDeviceCodeUrl(iframeRef.current, WALTER_HOST);
      }
    }, deviceCodeExpiryDurationMS);

    return () => {
      clearInterval(intervalId);
    };
  }, [deviceCodeExpiryDurationMS]);

  useEffect(() => {
    if (signInLoaded && iframeRef.current) {
      requestDeviceCodeUrl(iframeRef.current, WALTER_HOST);
    } else if (!iframeRef.current && signInLoaded) {
      setError("Something unexpected happened and we've been notified.");
      captureErrorInfo(new Error("Add-in sign in page loaded but iframeRef.current not set"));
    }
  }, [signInLoaded]);

  // Set up auth message handler
  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setError("Something unexpected happened and we've been notified.");
      captureErrorInfo(new Error("Sign in page did not load after 10 seconds."), {
        extra: { parentDomain: getParentDomain() },
      });
    }, 10000);

    const messageHandler = createAuthMessageHandler(WALTER_HOST, {
      onSignInPageLoaded: () => {
        setSignInLoaded(true);

        window.clearTimeout(timeoutId);
      },
      onDeviceCodeReceived: (url, expiresInSeconds) => {
        setDeviceAuthSignInUrl(url);
        setDeviceCodeExpiryDurationMS(expiresInSeconds * 1000);
      },
      onTokenReceived: (tokenData, _user, defaultWorkspaceId) => {
        tokenStorage.add("walter", tokenData);

        if (!defaultWorkspaceId) {
          showErrors({
            title: "Unable to continue without a Walter workspace.",
            messages: ["Please add a workspace for your user in Walter."],
          });
          captureErrorInfo(new Error("User without workspace attempted to log in"), { level: "info" });
          return;
        }

        setIsLoggedIn(true);
      },
      onTokenError: () => {
        showErrors({
          title: "Sign in failed",
          messages: ["Please try again."],
        });
      },
    });

    window.addEventListener("message", messageHandler);

    return () => {
      window.removeEventListener("message", messageHandler);
      clearTimeout(timeoutId);
    };
  }, [iframeRef, setIsLoggedIn, showErrors]);

  if (error) {
    return (
      <section className={mergeClasses(styles.inner, styles.textCenter)}>
        <Title3>Failed to load</Title3>

        <p>
          {error} Try{" "}
          <Link href="" type="button" onClick={() => window.location.reload()}>
            refreshing the page
          </Link>{" "}
          to resolve the error.
        </p>
        <p>
          If this issue persists please reach out to us at{" "}
          <Link rel="noopener" href="https://support.getwalter.com" target="_blank">
            https://support.getwalter.com
          </Link>
        </p>
      </section>
    );
  }

  return (
    <>
      <Toaster toasterId={toasterId} position="top" pauseOnHover pauseOnWindowBlur />
      {children}
      <iframe ref={iframeRef} src={deviceAutoSignInUrl} hidden />
      <div className={styles.inner}>
        {!deviceAuthSignInUrl && <Progress message="Initializing..." />}
        {deviceAuthSignInUrl && (
          <>
            <div className={styles.hero}>
              <img src={walterLogo} alt="Walter" className={styles.logo} />
              <Subtitle1>Walter</Subtitle1>
              <Caption1 className={styles.tagline}>The AI work platform for lawyers</Caption1>
            </div>
            <div className={styles.actions}>
              <Button as="a" href={deviceAuthSignInUrl} size="large" appearance="primary" target="_blank">
                Sign in
              </Button>
            </div>
            <div className={styles.footer}>
              <Caption1 className={styles.footerText}>
                <Link href="https://support.getwalter.com" target="_blank">
                  Help & Support
                </Link>
              </Caption1>
            </div>
          </>
        )}
      </div>
    </>
  );
};
